/**
 * The single IN-PROCESS choke point (impl-expectation #1; AC 14.4). Every edit routed through
 * editor-core — human or AI — enters here and becomes exactly one attributed Yjs transaction
 * (act lane) or one reviewable proposal (propose lane).
 *
 * SCOPE CAVEAT (AC 5.4 / 7.5): the per-span attribution mark and the fine-grained act/propose lane
 * floor are a client/library convention. As of Pass 4 the v2 Hocuspocus room DOES enforce the
 * CAPABILITY ACL at the transport (a socket denied `update` is forced read-only via
 * `/api/v2/documents.write-decision`), so a resource-`read_write`-but-capability-denied user can no
 * longer write the CRDT directly. The residual — server-side verification of the attribution mark
 * and the per-span lane floor — is an ACCEPTED LOW residual for this cut (blocked on a finalized
 * lane policy, OQ-3, and on an origin signal the CRDT does not carry). See
 * `Documentation/ai-map/features/parallel-editor-v2/security-review-addendum.md`.
 *
 * The most-restrictive safety floor is applied HERE as well as in the resolver, so an edit
 * routed through this path can never reach the live `act` lane by bypassing lane resolution (OQ-3).
 */

import type { EngineHandle } from '../engine/handle';
import type { LaneResolver } from '../lanes/LaneResolver';
import { mostRestrictive } from '../lanes/LaneResolver';
import type { ApplyResult, Attribution, EditIntent, Lane, Proposal } from '../types';

let proposalCounter = 0;

/** Deterministic-enough id without Math.random (kept stable per session/engine instance). */
function nextProposalId(participantId: string): string {
  proposalCounter += 1;
  return `prop-${participantId.replace(/[^a-z0-9]/gi, '')}-${proposalCounter}`;
}

export interface ApplyDeps {
  engine: EngineHandle;
  resolver: LaneResolver;
  /** Stable timestamp source (injected so the engine stays deterministic/testable). */
  now(): string;
}

export function applyEdit(deps: ApplyDeps, intent: EditIntent): ApplyResult {
  const resolved = deps.resolver.resolve(intent.scope, intent.requestedLane);
  // Belt-and-braces floor: even if a caller reached here with a looser requestedLane than the
  // resolver returned, we clamp again to the more restrictive of the two.
  const lane: Lane = intent.requestedLane ? mostRestrictive(resolved, intent.requestedLane) : resolved;

  const attribution: Attribution = {
    participant: intent.participant,
    lane,
    ts: deps.now(),
  };

  if (lane === 'act') {
    deps.engine.transactAttributed(intent.range, intent.op, intent.text, attribution);
    return { applied: true, lane };
  }

  // propose lane: reviewable, never mutates live content until accepted (AC 6.4). The proposal
  // carries the payload + rationale so acceptance can apply it verbatim later.
  const proposal: Proposal = {
    proposalId: nextProposalId(intent.participant.id),
    author: intent.participant,
    range: intent.range,
    op: intent.op,
    status: 'pending',
    text: intent.text,
    rationale: intent.rationale,
  };
  deps.engine.addProposal(proposal);
  return { applied: false, lane, proposal };
}

/**
 * Accept a pending proposal — this too goes through the single write path: acceptance applies
 * the proposal as a normal attributed `act` transaction (AC 6.4).
 */
export function acceptProposal(deps: ApplyDeps, proposal: Proposal, text: string | undefined): ApplyResult {
  const attribution: Attribution = { participant: proposal.author, lane: 'act', ts: deps.now() };
  // No explicit override → apply the payload stored on the proposal itself.
  deps.engine.transactAttributed(proposal.range, proposal.op, text ?? proposal.text, attribution);
  return { applied: true, lane: 'act' };
}
